/* Import TinyMCE */
import tinymce from 'tinymce';
import {config} from "./config.js";

/* Default icons are required. After that, import custom icons if applicable */
import 'tinymce/icons/default';

/* Required TinyMCE components */
import 'tinymce/themes/silver';
import 'tinymce/models/dom';

/* Import language */
import './util/language.js';

/* Import plugins */
import 'tinymce/plugins/lists';
import 'tinymce/plugins/table';
import 'tinymce/plugins/codesample';
import 'tinymce/plugins/emoticons';
import 'tinymce/plugins/emoticons/js/emojis';
import 'tinymce/plugins/link';
import 'tinymce/plugins/image';
import 'tinymce/plugins/autolink';
import 'tinymce/plugins/autoresize';
import 'tinymce/plugins/anchor';
import 'tinymce/plugins/directionality';
import './plugins/autosavecursor/plugin.js';
import './plugins/blockquotepatch/plugin.js';
import './plugins/cannedresponses/plugin.js';
import './plugins/codemirror/plugin.js';
import './plugins/content/plugin.js';
import './plugins/disable_shortcuts/plugin.js';
import './plugins/limiter/plugin.js';
import './plugins/mentions/plugin.js';
import './plugins/mergefields/plugin.js';
import './plugins/paste/plugin.js';
import './plugins/selfservice/plugin.js';
import {mergeConfigs} from "./util/util.js";

(function () {
  var undef, patchApplied;
  var $, win;

  win = typeof global !== 'undefined' ? global : window;
  $ = win.jQuery;

  var getTinymce = function () {
    return tinymce;
  };

  $.fn.editor = function (settings) {
    var self = this, url, base, lang, suffix = "";

    // No match then just ignore the call
    if (!self.length) {
      return self;
    }

    // If initialised, return the editor instance.
    if (getTinymce() && getTinymce().get(self[0].id)) {
      return getTinymce() ? getTinymce().get(self[0].id) : null;
    }

    self.css('visibility', 'hidden'); // Hide textarea to avoid flicker

    var init = function () {
      var editors = [], initCount = 0;

      // Apply patches to the jQuery object, only once
      if (!patchApplied) {
        applyPatch();
        patchApplied = true;
      }

      // Create an editor instance for each matched node
      self.each(function (i, node) {
        var ed, id = node.id;

        // Generate unique id for target element if needed
        if (!id) {
          node.id = id = getTinymce().DOM.uniqueId();
        }

        // Only init the editor once
        if (getTinymce().get(id)) {
          return;
        }

        var options = mergeConfigs($.fn.editor.defaults, settings);
        $(node).trigger('editor:init', options);

        // Create editor instance and render it
        ed = getTinymce().createEditor(id, options);
        editors.push(ed);

        $(node).trigger('editor:create', ed);

        ed.on('init', function () {
          self.css('visibility', '');
        });
      });

      // Render the editor instances in a separate loop since we
      // need to have the full editors array used in the onInit calls
      $.each(editors, function (i, ed) {
        ed.render();
      });
    };

    init();

    return self;
  };

  $.fn.editor.defaults = config;

  // Add :tinymce pseudo selector this will select elements that has been converted into editor instances
  // it's now possible to use things like $('*:tinymce') to get all TinyMCE bound elements.
  $.extend($.expr.pseudos || $.expr[':'], {
    tinymce: function (e) {
      var editor;

      if (e.id && "tinymce" in win) {
        editor = getTinymce().get(e.id);

        if (editor && editor.editorManager === getTinymce()) {
          return true;
        }
      }

      return false;
    }
  });

  // This function patches internal jQuery functions so that if
  // you for example remove an div element containing an editor it's
  // automatically destroyed by the TinyMCE API
  var applyPatch = function () {
    // Removes any child editor instances by looking for editor wrapper elements
    var removeEditors = function (name) {
      // If the function is remove
      if (name === "remove") {
        this.each(function (i, node) {
          var ed = tinyMCEInstance(node);

          if (ed) {
            ed.remove();
          }
        });
      }

      this.find("span.mceEditor,div.mceEditor").each(function (i, node) {
        var ed = getTinymce().get(node.id.replace(/_parent$/, ""));

        if (ed) {
          ed.remove();
        }
      });
    };

    // Loads or saves contents from/to textarea if the value
    // argument is defined it will set the TinyMCE internal contents
    var loadOrSave = function (value) {
      var self = this, ed;

      // Handle set value
      /*jshint eqnull:true */
      if (value != null) {
        removeEditors.call(self);

        // Saves the contents before get/set value of textarea/div
        self.each(function (i, node) {
          var ed;

          if ((ed = getTinymce().get(node.id))) {
            ed.setContent(value);
          }
        });
      } else if (self.length > 0) {
        // Handle get value
        if ((ed = getTinymce().get(self[0].id))) {
          return ed.getContent();
        }
      }
    };

    // Returns tinymce instance for the specified element or null if it wasn't found
    var tinyMCEInstance = function (element) {
      var ed = null;

      if (element && element.id && win.tinymce) {
        ed = getTinymce().get(element.id);
      }

      return ed;
    };

    // Checks if the specified set contains tinymce instances
    var containsTinyMCE = function (matchedSet) {
      return !!((matchedSet) && (matchedSet.length) && (win.tinymce) && (matchedSet.is(":tinymce")));
    };

    // Patch various jQuery functions
    var jQueryFn = {};

    // Patch some setter/getter functions these will
    // now be able to set/get the contents of editor instances for
    // example $('#editorid').html('Content'); will update the TinyMCE iframe instance
    $.each(["text", "html", "val"], function (i, name) {
      var origFn = jQueryFn[name] = $.fn[name],
        textProc = (name === "text");

      $.fn[name] = function (value) {
        var self = this;

        if (!containsTinyMCE(self)) {
          return origFn.apply(self, arguments);
        }

        if (value !== undef) {
          loadOrSave.call(self.filter(":tinymce"), value);
          origFn.apply(self.not(":tinymce"), arguments);

          return self; // return original set for chaining
        }

        var ret = "";
        var args = arguments;

        (textProc ? self : self.eq(0)).each(function (i, node) {
          var ed = tinyMCEInstance(node);

          if (ed) {
            ret += textProc ? ed.getContent().replace(/<(?:"[^"]*"|'[^']*'|[^'">])*>/g, "") : ed.getContent({ save: true });
          } else {
            ret += origFn.apply($(node), args);
          }
        });

        return ret;
      };
    });

    // Makes it possible to use $('#id').append("content"); to append contents to the TinyMCE editor iframe
    $.each(["append", "prepend"], function (i, name) {
      var origFn = jQueryFn[name] = $.fn[name],
        prepend = (name === "prepend");

      $.fn[name] = function (value) {
        var self = this;

        if (!containsTinyMCE(self)) {
          return origFn.apply(self, arguments);
        }

        if (value !== undef) {
          if (typeof value === "string") {
            self.filter(":tinymce").each(function (i, node) {
              var ed = tinyMCEInstance(node);

              if (ed) {
                ed.setContent(prepend ? value + ed.getContent() : ed.getContent() + value);
              }
            });
          }

          origFn.apply(self.not(":tinymce"), arguments);

          return self; // return original set for chaining
        }
      };
    });

    // Makes sure that the editor instance gets properly destroyed when the parent element is removed
    $.each(["remove", "replaceWith", "replaceAll", "empty"], function (i, name) {
      var origFn = jQueryFn[name] = $.fn[name];

      $.fn[name] = function () {
        removeEditors.call(this, name);

        return origFn.apply(this, arguments);
      };
    });

    jQueryFn.attr = $.fn.attr;

    // Makes sure that $('#tinymce_id').attr('value') gets the editors current HTML contents
    $.fn.attr = function (name, value) {
      var self = this, args = arguments;

      if ((!name) || (name !== "value") || (!containsTinyMCE(self))) {
        if (value !== undef) {
          return jQueryFn.attr.apply(self, args);
        }

        return jQueryFn.attr.apply(self, args);
      }

      if (value !== undef) {
        loadOrSave.call(self.filter(":tinymce"), value);
        jQueryFn.attr.apply(self.not(":tinymce"), args);

        return self; // return original set for chaining
      }

      var node = self[0], ed = tinyMCEInstance(node);

      return ed ? ed.getContent({ save: true }) : jQueryFn.attr.apply($(node), args);
    };
  };
})();
